import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { HiveLinkedService, hiveAuthTypes } from 'app/model/linkedservice/hive.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ReferenceAdapter } from '../reference.adapter';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HiveLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                port: '*',
                serverType: '*',
                thriftTransportProtocol: '*',
                authenticationType: '*',
                serviceDiscoveryMode: '*',
                zooKeeperNameSpace: '*',
                useNativeQuery: '*',
                username: '*',
                password: 'secret',
                httpPath: '*',
                enableSsl: '*',
                trustedCertPath: '*',
                useSystemTrustStore: '*',
                allowHostNameCNMismatch: '*',
                allowSelfSignedServerCert: '*',
                enableServerCertificateValidation: '*',
                encryptedCredential: '*',
                storageReference: {
                    referenceName: '*',
                    type: '*'
                }
            }
        }
    }
})
export class HiveLinkedServiceAdapter extends LinkedServiceAdapter<HiveLinkedService> {
    protected modelConstructor(): new () => HiveLinkedService {
        return HiveLinkedService;
    }

    protected model(): IModel {
        return HiveLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: HiveLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.HiveLinkedService;
        properties.type = Metamodel.modelFor(HiveLinkedService).name;
        const password: Model.SecretBase = (linkedService.authenticationType === hiveAuthTypes.UsernameAndPassword || linkedService.authenticationType === hiveAuthTypes.WindowsAzureHDInsightService)
            && passwordOrAKVtoSecretBase(linkedService.password, this.context);
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                enableServerCertificateValidation: linkedService.enableSsl ? linkedService.enableServerCertificateValidation : undefined,
                password
            };
        } else {
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                password,
                serviceDiscoveryMode: linkedService.serviceDiscoveryMode,
                zooKeeperNameSpace: linkedService.zooKeeperNameSpace ? linkedService.zooKeeperNameSpace : undefined,
                useNativeQuery: linkedService.useNativeQuery,
                trustedCertPath: linkedService.enableSsl && linkedService.trustedCertPath ? linkedService.trustedCertPath : undefined,
                useSystemTrustStore: linkedService.enableSsl ? linkedService.useSystemTrustStore : undefined,
                allowHostNameCNMismatch: linkedService.enableSsl ? linkedService.allowHostNameCNMismatch : undefined,
                allowSelfSignedServerCert: linkedService.enableSsl ? linkedService.allowSelfSignedServerCert : undefined,
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: HiveLinkedService): Promise<HiveLinkedService> {
        const properties = linkedServiceResource.properties as Model.HiveLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.HiveLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version == CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        const isV2 = properties.version === CONNECTOR_VERSION20;
        linkedService.isLegacyConnector = !isV2;
        linkedService.host = typeProperties.host;
        linkedService.port = typeProperties.port;
        linkedService.serverType = typeProperties.serverType;
        linkedService.thriftTransportProtocol = typeProperties.thriftTransportProtocol;
        linkedService.authenticationType = typeProperties.authenticationType;
        linkedService.serviceDiscoveryMode = typeProperties.serviceDiscoveryMode;
        linkedService.zooKeeperNameSpace = typeProperties.zooKeeperNameSpace;
        linkedService.useNativeQuery = typeProperties.useNativeQuery;
        linkedService.username = typeProperties.username;
        linkedService.password = secretBaseToPasswordOrAKV(
            typeProperties.password,
            typeProperties.authenticationType === hiveAuthTypes.UsernameAndPassword || typeProperties.authenticationType === hiveAuthTypes.WindowsAzureHDInsightService, this.context
        );
        linkedService.httpPath = typeProperties.httpPath;
        linkedService.enableSsl = typeProperties.enableSsl;
        //v1
        linkedService.trustedCertPath = isV2 ? undefined : typeProperties.trustedCertPath;
        linkedService.useSystemTrustStore = isV2 ? undefined : typeProperties.useSystemTrustStore;
        linkedService.allowHostNameCNMismatch = isV2 ? undefined :typeProperties.allowHostNameCNMismatch;
        linkedService.allowSelfSignedServerCert = isV2 ? undefined :typeProperties.allowSelfSignedServerCert;
        //v2
        linkedService.enableServerCertificateValidation = isV2 ? typeProperties.enableServerCertificateValidation : undefined;
        
        if (!!properties.typeProperties.storageReference) {
            linkedService.azureStorageLinkedService = linkedService.azureStorageLinkedService ||
                ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(Metamodel.modelFor(AzureBlobStorageLinkedService),
                properties.typeProperties.storageReference, this.context);
        }
        return linkedService;
    }
    private _commonProperties(linkedService: HiveLinkedService): Model.HiveLinkedServiceTypeProperties {
        return {
            host: linkedService.host,
            port: Number(linkedService.port),
            serverType: linkedService.serverType,
            thriftTransportProtocol: linkedService.thriftTransportProtocol,
            authenticationType: linkedService.authenticationType,
            username: linkedService.authenticationType !== hiveAuthTypes.Anonymous ? linkedService.username : undefined,
            httpPath: linkedService.httpPath || undefined,
            enableSsl: linkedService.enableSsl,
            storageReference: linkedService.azureStorageLinkedService ?
                ReferenceAdapter.toResource(linkedService.azureStorageLinkedService, this.context) as Model.LinkedServiceReference :
                undefined
        };
    }
}
